import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Users, MessageSquare, Inbox, Send, ArrowUpRight, LogOut, Wifi, FileText, FolderKanban } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { useHeartbeat } from "../hooks/useHeartbeat";
import api from "../lib/api";
import { toast } from "sonner";

export default function Admin() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  useHeartbeat();

  const [tab, setTab] = useState("overview");

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col lg:flex-row">
      <aside className="lg:w-72 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/10 flex lg:flex-col bg-zinc-950">
        <div className="p-6 lg:p-8 hidden lg:block">
          <Link to="/" className="flex items-center gap-2 mb-12">
            <span className="block w-2.5 h-2.5 bg-emerald-400" />
            <span className="font-display text-xl">
              Versant<span className="text-zinc-500">.</span>Admin
            </span>
          </Link>

          <div className="mb-10 pb-6 border-b border-white/10">
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Créateur</p>
            <p className="text-sm font-medium text-zinc-100 truncate">{user?.name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>

          <nav className="space-y-1">
            <Item icon={Wifi} active={tab === "overview"} onClick={() => setTab("overview")} testid="admin-tab-overview">
              {t("admin.tab_overview")}
            </Item>
            <Item icon={MessageSquare} active={tab === "inbox"} onClick={() => setTab("inbox")} testid="admin-tab-inbox">
              {t("admin.tab_inbox")}
            </Item>
            <Item icon={Users} active={tab === "users"} onClick={() => setTab("users")} testid="admin-tab-users">
              {t("admin.tab_users")}
            </Item>
            <Item icon={Inbox} active={tab === "contacts"} onClick={() => setTab("contacts")} testid="admin-tab-contacts">
              {t("admin.tab_contacts")}
            </Item>
          </nav>
        </div>

        {/* Mobile horizontal */}
        <div className="lg:hidden flex items-center gap-1 overflow-x-auto p-3 w-full">
          {[
            ["overview", t("admin.tab_overview")],
            ["inbox", t("admin.tab_inbox")],
            ["users", t("admin.tab_users")],
            ["contacts", t("admin.tab_contacts")],
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-mobile-tab-${id}`}
              className={`flex-shrink-0 px-4 py-2 text-xs uppercase tracking-[0.15em] ${
                tab === id ? "bg-white text-zinc-950" : "text-zinc-400 border border-white/10"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="hidden lg:block mt-auto p-6 border-t border-white/10 space-y-2">
          <Link to="/" className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white">
            <ArrowUpRight size={12} /> {t("common.back_home")}
          </Link>
          <button
            data-testid="admin-logout"
            onClick={async () => {
              navigate("/", { replace: true });
              await logout();
            }}
            className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white"
          >
            <LogOut size={12} /> {t("common.logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 min-h-screen">
        <div className="border-b border-white/10 px-6 sm:px-12 py-8">
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">{t("admin.title")}</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter">
            {tab === "overview" && t("admin.tab_overview")}
            {tab === "inbox" && t("admin.tab_inbox")}
            {tab === "users" && t("admin.tab_users")}
            {tab === "contacts" && t("admin.tab_contacts")}
          </h1>
        </div>

        <div className="px-6 sm:px-12 py-10">
          {tab === "overview" && <OverviewSection />}
          {tab === "inbox" && <InboxSection />}
          {tab === "users" && <UsersSection />}
          {tab === "contacts" && <ContactsSection />}
        </div>
      </main>
    </div>
  );
}

const Item = ({ icon: Icon, active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
      active ? "bg-emerald-400 text-zinc-950" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
    }`}
  >
    <Icon size={16} strokeWidth={1.5} />
    {children}
  </button>
);

// ---------------- OVERVIEW ----------------

const OverviewSection = () => {
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [online, setOnline] = useState([]);

  const refresh = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/admin/stats"), api.get("/admin/online-users")]);
      setStats(a.data);
      setOnline(b.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    refresh();
    const t1 = setInterval(refresh, 8000);
    return () => clearInterval(t1);
  }, []);

  const cards = stats
    ? [
        { id: "users", label: t("admin.stat_users"), value: stats.users, icon: Users },
        { id: "quotes", label: t("admin.stat_quotes"), value: stats.quotes, icon: FileText },
        { id: "projects", label: t("admin.stat_projects"), value: stats.projects, icon: FolderKanban },
        { id: "contacts", label: t("admin.stat_contacts"), value: stats.contacts, icon: Inbox },
        { id: "unread", label: t("admin.stat_unread"), value: stats.unread_messages, icon: MessageSquare },
      ]
    : [];

  return (
    <div className="space-y-10" data-testid="admin-overview">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.id} className="bg-zinc-950 p-6">
              <div className="flex items-center justify-between mb-6">
                <Icon size={16} strokeWidth={1.4} className="text-zinc-400" />
                <span className="font-mono text-[10px] text-zinc-600">·</span>
              </div>
              <p className="font-display text-3xl tracking-tighter">{c.value}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">{c.label}</p>
            </div>
          );
        })}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("admin.online_users")}</p>
          <span className="font-mono text-xs text-emerald-400">
            {online.length} en ligne
          </span>
        </div>
        {online.length === 0 ? (
          <div className="border border-white/10 border-dashed p-12 text-center">
            <p className="text-zinc-400 text-sm">{t("admin.no_online")}</p>
          </div>
        ) : (
          <div className="border border-white/10 divide-y divide-white/10" data-testid="admin-online-list">
            {online.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between p-5 bg-zinc-950">
                <div className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-emerald-400 inline-block animate-pulse" />
                  <div>
                    <p className="text-sm">{u.name}</p>
                    <p className="text-xs text-zinc-500">{u.email}</p>
                  </div>
                </div>
                <p className="font-mono text-[10px] text-zinc-500">
                  {new Date(u.last_seen).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------- INBOX ----------------

const InboxSection = () => {
  const { t } = useI18n();
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadConvs = async () => {
    try {
      const { data } = await api.get("/admin/conversations");
      setConversations(data);
    } catch {
      // ignore
    }
  };

  const loadMessages = async (uid) => {
    try {
      const { data } = await api.get(`/admin/messages/${uid}`);
      setMessages(data);
    } catch {
      setMessages([]);
    }
  };

  useEffect(() => {
    loadConvs();
    const t1 = setInterval(loadConvs, 6000);
    return () => clearInterval(t1);
  }, []);

  useEffect(() => {
    if (!selected) return undefined;
    loadMessages(selected.user_id);
    const t1 = setInterval(() => loadMessages(selected.user_id), 4000);
    return () => clearInterval(t1);
  }, [selected]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await api.post("/admin/reply", { user_id: selected.user_id, content: reply.trim() });
      setReply("");
      await loadMessages(selected.user_id);
      await loadConvs();
      toast.success("Réponse envoyée.");
    } catch {
      toast.error("Erreur");
    } finally {
      setSending(false);
    }
  };

  const initials = (selected?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div data-testid="admin-inbox" className="border border-white/10 grid grid-cols-1 lg:grid-cols-12">
      <aside className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-white/10 bg-zinc-900/30 max-h-[700px] overflow-y-auto">
        <p className="px-5 py-4 text-xs uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10">
          {t("admin.conversations")} · {conversations.length}
        </p>
        {conversations.length === 0 ? (
          <p className="p-5 text-sm text-zinc-500">{t("admin.no_conv")}</p>
        ) : (
          <ul className="divide-y divide-white/10" data-testid="admin-conv-list">
            {conversations.map((c) => (
              <li key={c.user_id}>
                <button
                  data-testid={`admin-conv-${c.user_id}`}
                  onClick={() => setSelected(c)}
                  className={`w-full text-left p-4 transition-colors ${
                    selected?.user_id === c.user_id ? "bg-zinc-900" : "hover:bg-zinc-900/60"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-zinc-800 flex-shrink-0 flex items-center justify-center font-display text-xs">
                      {(c.name || "?")
                        .split(" ")
                        .map((s) => s[0])
                        .slice(0, 2)
                        .join("")
                        .toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        {c.unread > 0 && (
                          <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 bg-emerald-400 text-zinc-950 text-[10px] font-mono">
                            {c.unread}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 truncate">{c.last_message}</p>
                      <p className="text-[10px] text-zinc-600 mt-1 font-mono">
                        {c.last_sender === "user" ? "·" : "you ·"} {new Date(c.last_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <div className="lg:col-span-8 flex flex-col h-[700px]">
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-sm text-zinc-500 p-10 text-center">
            {t("admin.select_conv")}
          </div>
        ) : (
          <>
            <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3 bg-zinc-950">
              <div className="w-10 h-10 bg-zinc-800 flex items-center justify-center font-display text-sm">
                {initials}
              </div>
              <div>
                <p className="text-sm font-medium">{selected.name}</p>
                <p className="text-xs text-zinc-500">{selected.email}</p>
              </div>
            </div>
            <div ref={scrollRef} data-testid="admin-msgs" className="flex-1 overflow-y-auto p-6 space-y-4 bg-zinc-950">
              {messages.map((m) => {
                const isAdmin = m.sender === "admin";
                return (
                  <div key={m.message_id} className={`flex gap-3 ${isAdmin ? "justify-end" : "justify-start"}`}>
                    {!isAdmin && (
                      <div className="w-8 h-8 bg-zinc-800 flex-shrink-0 flex items-center justify-center font-display text-xs">
                        {initials}
                      </div>
                    )}
                    <div className={`max-w-[75%] px-4 py-3 text-sm leading-relaxed ${
                      isAdmin ? "bg-emerald-400 text-zinc-950" : "bg-zinc-900 text-zinc-100 border border-white/10"
                    }`}>
                      {m.content}
                      <div className={`text-[10px] mt-1.5 font-mono ${isAdmin ? "text-zinc-700" : "text-zinc-500"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="w-8 h-8 bg-emerald-400 text-zinc-950 flex-shrink-0 flex items-center justify-center font-display text-xs">
                        VS
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <form onSubmit={send} data-testid="admin-reply-form" className="border-t border-white/10 p-4 flex items-center gap-3 bg-zinc-950">
              <input
                data-testid="admin-reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={t("admin.reply_placeholder")}
                className="flex-1 bg-zinc-900 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white"
              />
              <button
                type="submit"
                disabled={sending || !reply.trim()}
                data-testid="admin-reply-send"
                className="inline-flex items-center gap-2 bg-emerald-400 text-zinc-950 hover:bg-emerald-300 px-5 py-3 text-sm disabled:opacity-50"
              >
                <Send size={14} />
                {t("admin.reply_send")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------- USERS ----------------

const UsersSection = () => {
  const { t } = useI18n();
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-users" className="border border-white/10">
      <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 text-xs uppercase tracking-[0.2em] text-zinc-500 bg-zinc-900/30">
        <div className="col-span-4">Nom</div>
        <div className="col-span-4">Email</div>
        <div className="col-span-2">Provider</div>
        <div className="col-span-2">Vérifié</div>
      </div>
      {users.length === 0 ? (
        <div className="p-12 text-center text-sm text-zinc-500">Aucun utilisateur.</div>
      ) : (
        <ul className="divide-y divide-white/10">
          {users.map((u) => (
            <li key={u.user_id} className="grid grid-cols-12 gap-4 p-4 text-sm bg-zinc-950">
              <div className="col-span-4">{u.name}</div>
              <div className="col-span-4 text-zinc-400">{u.email}</div>
              <div className="col-span-2 font-mono text-xs uppercase text-zinc-500">{u.auth_provider}</div>
              <div className="col-span-2">
                {u.email_verified ? (
                  <span className="text-emerald-400 text-xs">✓ vérifié</span>
                ) : (
                  <span className="text-amber-300 text-xs">en attente</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ---------------- CONTACTS ----------------

const ContactsSection = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/contacts").then((r) => setItems(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="admin-contacts">
      {items.length === 0 ? (
        <div className="border border-white/10 border-dashed p-12 text-center">
          <p className="text-zinc-400 text-sm">{t("admin.no_contacts")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <article key={c.id} className="border border-white/10 p-5 bg-zinc-950">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-zinc-500 font-mono">{c.email} · {c.phone || "—"}</p>
                </div>
                <p className="text-xs text-zinc-500 font-mono">
                  {new Date(c.created_at).toLocaleDateString()}
                </p>
              </div>
              <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{c.message}</p>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
